import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Layers, Loader2, Trash2, Download, CheckSquare, Square } from "lucide-react";

const THEME_PREVIEWS = {
  midnight: "from-slate-900 via-indigo-950 to-slate-900",
  emerald: "from-emerald-900 via-teal-900 to-slate-900",
  sunset: "from-rose-900 via-orange-900 to-slate-900",
  royal: "from-violet-900 via-purple-950 to-slate-900",
  graphite: "from-zinc-800 via-zinc-900 to-black",
};

export default function BulkManagement() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    base44.entities.Card.list("-created_date", 200).then(setCards).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleAll = () => {
    if (selected.size === cards.length) setSelected(new Set());
    else setSelected(new Set(cards.map((c) => c.id)));
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const deleteSelected = async () => {
    if (!confirm(`Delete ${selected.size} card(s)?`)) return;
    setDeleting(true);
    await Promise.all([...selected].map((id) => base44.entities.Card.delete(id)));
    await base44.entities.ActivityLog.create({ action: "card_deleted", entity_type: "Card", details: `Bulk deleted ${selected.size} cards` });
    setSelected(new Set());
    load();
    setDeleting(false);
  };

  const exportSelected = () => {
    const data = cards.filter((c) => selected.has(c.id)).map(({ id, cardholder_name, masked_number, last4, brand, expiry_month, expiry_year, nickname, token, created_date }) =>
      ({ id, cardholder_name, masked_number, last4, brand, expiry_month, expiry_year, nickname, token, created_date }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "voult-export.json"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Bulk Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">{selected.size} of {cards.length} selected</p>
        </div>
        {selected.size > 0 && (
          <div className="flex gap-2">
            <button onClick={exportSelected}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
              <Download className="h-4 w-4" /> Export
            </button>
            <button onClick={deleteSelected} disabled={deleting}
              className="inline-flex items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-rose-700 transition disabled:opacity-50">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete {selected.size}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : cards.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Layers className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>No cards to manage.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50">
            <button onClick={toggleAll}>
              {selected.size === cards.length && cards.length > 0
                ? <CheckSquare className="h-4.5 w-4.5 text-slate-900" />
                : <Square className="h-4.5 w-4.5 text-slate-400" />}
            </button>
            <span className="text-xs font-medium text-slate-500">Select all</span>
          </div>
          {cards.map((card) => (
            <div key={card.id} onClick={() => toggle(card.id)}
              className={`flex items-center gap-4 px-5 py-3.5 border-b border-slate-100 last:border-0 cursor-pointer transition ${selected.has(card.id) ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
              <div className="flex-shrink-0">
                {selected.has(card.id)
                  ? <CheckSquare className="h-4.5 w-4.5 text-indigo-600" />
                  : <Square className="h-4.5 w-4.5 text-slate-300" />}
              </div>
              <div className={`h-10 w-16 flex-shrink-0 rounded-lg bg-gradient-to-br ${THEME_PREVIEWS[card.color_theme] || THEME_PREVIEWS.midnight}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{card.nickname || card.cardholder_name}</p>
                <p className="text-xs text-slate-500 font-mono">•••• {card.last4} · {card.brand}</p>
              </div>
              <p className="text-xs text-slate-400">{card.expiry_month}/{card.expiry_year}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}